import { useEffect, useState } from 'react';
import { buildEasyIntakeUpdate } from '../utils/easyModePayload';
import { validateEasyModeIntake } from '../utils/easyModeValidation';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useCaseTypeOptions from './useCaseTypeOptions';

const PLAINTIFF_COUNT_FIELD = 'plaintiffCount';
const DEFENDANT_COUNT_FIELD = 'defendantCount';

const buildIntakeValues = ({ caseData }) => ({
  caseInfo: {
    caseName: caseData?.caseInfo?.caseName ?? '',
    caseType: caseData?.caseInfo?.caseType ?? '',
    stateOfIncident: caseData?.caseInfo?.stateOfIncident ?? '',
    dateOfIncident: caseData?.caseInfo?.dateOfIncident ?? ''
  },
  // the counts are stored on the case rather than derived from the party arrays: nothing materializes
  // blanks any more, so clients[] stays at its single default until the agent resolves the documents
  plaintiffCount: caseData?.caseInfo?.plaintiffCount || 1,
  defendantCount: caseData?.caseInfo?.defendantCount || 1
});

const useEasyModeIntake = ({ caseData }) => {
  const { updateCase } = useCaseFieldUpdate();
  const caseTypeOptions = useCaseTypeOptions();
  const [values, setValues] = useState(() => buildIntakeValues({ caseData }));
  const [touched, setTouched] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // re-seeds after the initial load and after a partner import overwrites the case
  useEffect(() => {
    if (caseData) {
      setValues(buildIntakeValues({ caseData }));
    }
  }, [caseData]);

  const { errors } = validateEasyModeIntake({ ...values, caseTypeOptions });

  const markTouched = ({ name }) => setTouched((previous) => ({ ...previous, [name]: true }));

  const setPartyCount = ({ name, value }) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    markTouched({ name });
  };

  const handleCaseInfoChange = (event) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, caseInfo: { ...previous.caseInfo, [name]: value } }));
  };

  // field-group agnostic: the touched map is keyed by the flat field name, same as the error map
  const handleFieldBlur = (event) => markTouched({ name: event.target.name });

  const handlePlaintiffCountChange = ({ value }) =>
    setPartyCount({ name: PLAINTIFF_COUNT_FIELD, value });

  const handleDefendantCountChange = ({ value }) =>
    setPartyCount({ name: DEFENDANT_COUNT_FIELD, value });

  // errors stay hidden until the field is left, never on first paint
  const getFieldError = ({ name }) => (touched[name] ? errors[name] : '');

  const save = async () => {
    setIsSaving(true);

    try {
      return await updateCase({ data: buildEasyIntakeUpdate({ intakeValues: values }) });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    values,
    isSaving,
    getFieldError,
    handleCaseInfoChange,
    handleFieldBlur,
    handlePlaintiffCountChange,
    handleDefendantCountChange,
    save
  };
};

export default useEasyModeIntake;
